import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  TrendingUp, Plus, Target, Send, Trash2, Edit2, Clock, 
  Smile, AlertTriangle, Flame, Users, User, RefreshCw, Settings
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TodaySales = ({ user, onLogout }) => {
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  
  // Dialog states
  const [showAddOffer, setShowAddOffer] = useState(false);
  const [showEditSale, setShowEditSale] = useState(false);
  const [showSetTarget, setShowSetTarget] = useState(false);
  const [showSendNotification, setShowSendNotification] = useState(false);
  const [showManageOffers, setShowManageOffers] = useState(false);
  
  // Form states
  const [newOfferName, setNewOfferName] = useState('');
  const [editSaleData, setEditSaleData] = useState({ agentEmail: '', agentName: '', offerId: '', offerName: '', count: 0 });
  const [targetData, setTargetData] = useState({ type: 'agent', agentEmail: '', target: 0 });
  const [notificationData, setNotificationData] = useState({ type: 'motivational', recipientType: 'agent', recipientEmail: '', message: '' });
  
  const [agents, setAgents] = useState([]);
  const [teamTarget, setTeamTarget] = useState(0);

  const fetchSalesData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/today-sales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalesData(response.data);
      setOffers(response.data.offers || []);
      setAgents(response.data.agent_sales || []);
      setTeamTarget(response.data.team_target || 0);
    } catch (error) {
      toast.error('Failed to fetch sales data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSalesData();
    // Refresh every 5 seconds for real-time ranking updates
    const interval = setInterval(fetchSalesData, 5000);
    return () => clearInterval(interval);
  }, [fetchSalesData]);

  // Increment sale (Agent only)
  const handleIncrementSale = async (offerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/today-sales/increment`, 
        { offer_id: offerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sale recorded!');
      fetchSalesData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record sale');
    }
  };

  // Add offer (Manager/Admin)
  const handleAddOffer = async () => {
    if (!newOfferName.trim()) {
      toast.error('Please enter offer name');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/offers`, 
        { name: newOfferName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Offer added!');
      setNewOfferName('');
      setShowAddOffer(false);
      fetchSalesData();
    } catch (error) {
      toast.error('Failed to add offer');
    }
  };

  // Delete offer (Manager/Admin)
  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Delete this offer?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/offers/${offerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Offer deleted');
      fetchSalesData();
    } catch (error) {
      toast.error('Failed to delete offer');
    }
  };

  // Edit sale count (Manager/Admin)
  const handleEditSale = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/today-sales/edit`, {
        agent_email: editSaleData.agentEmail,
        offer_id: editSaleData.offerId,
        count: parseInt(editSaleData.count)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sale count updated');
      setShowEditSale(false);
      fetchSalesData();
    } catch (error) {
      toast.error('Failed to update sale count');
    }
  };

  // Set target (Manager/Admin)
  const handleSetTarget = async () => {
    try {
      const token = localStorage.getItem('token');
      if (targetData.type === 'team') {
        await axios.post(`${BACKEND_URL}/api/team-target`, {
          daily_target: parseInt(targetData.target)
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Team target set');
      } else {
        await axios.post(`${BACKEND_URL}/api/agent-targets`, {
          agent_email: targetData.agentEmail,
          daily_target: parseInt(targetData.target)
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Agent target set');
      }
      setShowSetTarget(false);
      fetchSalesData();
    } catch (error) {
      toast.error('Failed to set target');
    }
  };

  // Remove agent target (Manager/Admin)
  const handleRemoveTarget = async (agentEmail) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/agent-targets/${encodeURIComponent(agentEmail)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Target removed');
      fetchSalesData();
    } catch (error) {
      toast.error('Failed to remove target');
    }
  };

  // Send notification (Manager/Admin)
  const handleSendNotification = async () => {
    if (!notificationData.message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/performance-notifications`, {
        notification_type: notificationData.type,
        recipient_type: notificationData.recipientType,
        recipient_email: notificationData.recipientType === 'agent' ? notificationData.recipientEmail : null,
        message: notificationData.message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notification sent!');
      setShowSendNotification(false);
      setNotificationData({ type: 'motivational', recipientType: 'agent', recipientEmail: '', message: '' });
    } catch (error) {
      toast.error('Failed to send notification');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'motivational': return <Smile className="w-5 h-5 text-emerald-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'pressure': return <Flame className="w-5 h-5 text-red-500" />;
      default: return <Send className="w-5 h-5" />;
    }
  };

  const getProgressColor = (current, target) => {
    if (target === 0) return 'bg-slate-600';
    const percent = (current / target) * 100;
    if (percent >= 100) return 'bg-emerald-500';
    if (percent >= 75) return 'bg-amber-500';
    if (percent >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const isManager = user.role === 'manager' || user.role === 'admin';
  const isAgent = user.role === 'agent';

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="flex-1 p-6 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 p-6 overflow-auto" data-testid="today-sales-page">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Today Sales</h1>
                <div className="flex items-center gap-2 text-slate-400">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">{salesData?.date} • Resets at 5:00 PM IST</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchSalesData}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              {isManager && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowManageOffers(true)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Offers
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSetTarget(true)}
                    className="border-amber-500 text-amber-500 hover:bg-amber-500/10"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Set Target
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setShowSendNotification(true)}
                    className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Notification
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Team Target Card */}
          <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Target className="w-8 h-8 text-amber-500" />
                  <div>
                    <p className="text-slate-400 text-sm">Team Daily Target</p>
                    <p className="text-2xl font-bold text-white">{teamTarget} sales</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-slate-400 text-sm">Total Team Sales Today</p>
                  <p className="text-3xl font-bold text-emerald-400">
                    {agents.reduce((sum, a) => sum + a.total, 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Agent Quick Entry (For Agents Only) */}
          {isAgent && (
            <Card className="bg-gradient-to-br from-emerald-900/30 to-emerald-800/20 border-emerald-700/50">
              <CardHeader className="border-b border-emerald-700/50">
                <CardTitle className="text-white flex items-center gap-2">
                  <Plus className="w-5 h-5 text-emerald-500" />
                  Record Your Sale
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3">
                  {offers.map((offer) => (
                    <Button
                      key={offer.id}
                      onClick={() => handleIncrementSale(offer.id)}
                      className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white"
                      data-testid={`increment-${offer.id}`}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {offer.name}
                    </Button>
                  ))}
                  {offers.length === 0 && (
                    <p className="text-slate-400">No offers available. Contact your manager.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sales Table */}
          <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50">
            <CardHeader className="border-b border-slate-700/50">
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-500" />
                  Agent-wise Sales
                </span>
                <Badge variant="outline" className="text-slate-400 border-slate-600">
                  {agents.length} Agents
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-slate-700/50 bg-slate-800/50">
                      <TableHead className="text-slate-400 font-semibold">Agent</TableHead>
                      <TableHead className="text-slate-400 font-semibold text-center">Target</TableHead>
                      {offers.map((offer) => (
                        <TableHead key={offer.id} className="text-slate-400 font-semibold text-center">
                          {offer.name}
                        </TableHead>
                      ))}
                      <TableHead className="text-slate-400 font-semibold text-center">Total</TableHead>
                      <TableHead className="text-slate-400 font-semibold text-center">Progress</TableHead>
                      {isManager && (
                        <TableHead className="text-slate-400 font-semibold text-center">Actions</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agents.map((agent, index) => {
                      const progressPercent = agent.has_target && agent.target > 0 ? Math.min((agent.total / agent.target) * 100, 100) : 0;
                      return (
                        <TableRow 
                          key={agent.email} 
                          className={`border-b border-slate-700/50 hover:bg-slate-700/30 ${
                            agent.email === user.email ? 'bg-emerald-900/20' : ''
                          }`}
                        >
                          <TableCell className="text-white font-medium">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                index === 0 ? 'bg-amber-500 text-black' :
                                index === 1 ? 'bg-slate-400 text-black' :
                                index === 2 ? 'bg-amber-700 text-white' :
                                'bg-slate-600 text-white'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <p className="font-medium">{agent.name}</p>
                                <p className="text-xs text-slate-500">{agent.email}</p>
                              </div>
                              {agent.email === user.email && (
                                <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">You</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {agent.has_target ? (
                              <div className="flex items-center justify-center gap-1">
                                <Badge variant="outline" className="border-amber-500 text-amber-500">
                                  {agent.target}
                                </Badge>
                                {isManager && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveTarget(agent.email)}
                                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-1 h-6 w-6"
                                    title="Remove target"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-500 text-sm">-</span>
                            )}
                          </TableCell>
                          {offers.map((offer) => (
                            <TableCell key={offer.id} className="text-center">
                              <span className={`font-semibold ${
                                agent.sales_by_offer[offer.id] > 0 ? 'text-emerald-400' : 'text-slate-500'
                              }`}>
                                {agent.sales_by_offer[offer.id] || 0}
                              </span>
                            </TableCell>
                          ))}
                          <TableCell className="text-center">
                            <span className="text-xl font-bold text-white">{agent.total}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            {agent.has_target ? (
                              <div className="w-24 mx-auto">
                                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full ${getProgressColor(agent.total, agent.target)} transition-all duration-300`}
                                    style={{ width: `${progressPercent}%` }}
                                  />
                                </div>
                                <p className="text-xs text-slate-400 mt-1">
                                  {Math.round(progressPercent)}%
                                </p>
                              </div>
                            ) : (
                              <span className="text-slate-500 text-sm">No target</span>
                            )}
                          </TableCell>
                          {isManager && (
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditSaleData({ 
                                      agentEmail: agent.email, 
                                      agentName: agent.name,
                                      offerId: offers[0]?.id || '',
                                      offerName: offers[0]?.name || '',
                                      count: 0 
                                    });
                                    setShowEditSale(true);
                                  }}
                                  className="text-slate-400 hover:text-white hover:bg-slate-700"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setNotificationData({
                                      ...notificationData,
                                      recipientType: 'agent',
                                      recipientEmail: agent.email
                                    });
                                    setShowSendNotification(true);
                                  }}
                                  className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                                >
                                  <Send className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    {agents.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={offers.length + 5} className="text-center py-8 text-slate-400">
                          No agents found
                        </TableCell>
                      </TableRow>
                    )}
                    {/* Offer Totals Row */}
                    {offers.length > 0 && agents.length > 0 && (
                      <TableRow className="bg-amber-500/10 border-t-2 border-amber-500/50">
                        <TableCell className="text-amber-400 font-bold">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5" />
                            Team Total
                          </div>
                        </TableCell>
                        <TableCell className="text-center text-slate-400">-</TableCell>
                        {offers.map((offer) => (
                          <TableCell key={offer.id} className="text-center">
                            <span className="text-lg font-bold text-amber-400">
                              {salesData?.offer_totals?.[offer.id] || 0}
                            </span>
                          </TableCell>
                        ))}
                        <TableCell className="text-center">
                          <span className="text-2xl font-bold text-amber-400">
                            {agents.reduce((sum, a) => sum + a.total, 0)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center text-slate-400">-</TableCell>
                        {isManager && <TableCell className="text-center">-</TableCell>}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </main>

      {/* Manage Offers Dialog */}
      <Dialog open={showManageOffers} onOpenChange={setShowManageOffers}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-500" />
              Manage Offers
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Add or remove offers from the master list
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex gap-2">
              <Input
                value={newOfferName}
                onChange={(e) => setNewOfferName(e.target.value)}
                placeholder="Enter offer name..."
                className="bg-slate-900/50 border-slate-600 text-white flex-1"
              />
              <Button onClick={handleAddOffer} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {offers.map((offer) => (
                <div key={offer.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                  <span className="text-white">{offer.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteOffer(offer.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {offers.length === 0 && (
                <p className="text-slate-400 text-center py-4">No offers yet. Add one above.</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Sale Dialog */}
      <Dialog open={showEditSale} onOpenChange={setShowEditSale}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-amber-500" />
              Edit Sale Count
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Update sale count for {editSaleData.agentName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Select Offer</Label>
              <select
                value={editSaleData.offerId}
                onChange={(e) => {
                  const offer = offers.find(o => o.id === e.target.value);
                  setEditSaleData({ 
                    ...editSaleData, 
                    offerId: e.target.value,
                    offerName: offer?.name || ''
                  });
                }}
                className="w-full mt-2 p-2 rounded-md bg-slate-900/50 border border-slate-600 text-white"
              >
                {offers.map((offer) => (
                  <option key={offer.id} value={offer.id}>{offer.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-slate-300">Sale Count</Label>
              <Input
                type="number"
                value={editSaleData.count}
                onChange={(e) => setEditSaleData({ ...editSaleData, count: e.target.value })}
                className="bg-slate-900/50 border-slate-600 text-white mt-2"
                min="0"
              />
            </div>
            <Button onClick={handleEditSale} className="w-full bg-amber-600 hover:bg-amber-700">
              Update Sale Count
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set Target Dialog */}
      <Dialog open={showSetTarget} onOpenChange={setShowSetTarget}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-amber-500" />
              Set Daily Target
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Target Type</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  variant={targetData.type === 'team' ? 'default' : 'outline'}
                  onClick={() => setTargetData({ ...targetData, type: 'team' })}
                  className={targetData.type === 'team' ? 'bg-amber-600' : 'border-slate-600 text-slate-300'}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Team
                </Button>
                <Button
                  variant={targetData.type === 'agent' ? 'default' : 'outline'}
                  onClick={() => setTargetData({ ...targetData, type: 'agent' })}
                  className={targetData.type === 'agent' ? 'bg-amber-600' : 'border-slate-600 text-slate-300'}
                >
                  <User className="w-4 h-4 mr-2" />
                  Agent
                </Button>
              </div>
            </div>
            {targetData.type === 'agent' && (
              <div>
                <Label className="text-slate-300">Select Agent</Label>
                <select
                  value={targetData.agentEmail}
                  onChange={(e) => setTargetData({ ...targetData, agentEmail: e.target.value })}
                  className="w-full mt-2 p-2 rounded-md bg-slate-900/50 border border-slate-600 text-white"
                >
                  <option value="">Select an agent...</option>
                  {agents.map((agent) => (
                    <option key={agent.email} value={agent.email}>{agent.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <Label className="text-slate-300">Daily Target</Label>
              <Input
                type="number"
                value={targetData.target}
                onChange={(e) => setTargetData({ ...targetData, target: e.target.value })}
                className="bg-slate-900/50 border-slate-600 text-white mt-2"
                min="0"
                placeholder="Enter target number..."
              />
            </div>
            <Button onClick={handleSetTarget} className="w-full bg-amber-600 hover:bg-amber-700">
              Set Target
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Notification Dialog */}
      <Dialog open={showSendNotification} onOpenChange={setShowSendNotification}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-purple-500" />
              Send Performance Notification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Notification Type</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  variant={notificationData.type === 'motivational' ? 'default' : 'outline'}
                  onClick={() => setNotificationData({ ...notificationData, type: 'motivational' })}
                  className={notificationData.type === 'motivational' ? 'bg-emerald-600' : 'border-slate-600 text-slate-300'}
                >
                  <Smile className="w-4 h-4 mr-2" />
                  Motivational
                </Button>
                <Button
                  variant={notificationData.type === 'warning' ? 'default' : 'outline'}
                  onClick={() => setNotificationData({ ...notificationData, type: 'warning' })}
                  className={notificationData.type === 'warning' ? 'bg-amber-600' : 'border-slate-600 text-slate-300'}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Warning
                </Button>
                <Button
                  variant={notificationData.type === 'pressure' ? 'default' : 'outline'}
                  onClick={() => setNotificationData({ ...notificationData, type: 'pressure' })}
                  className={notificationData.type === 'pressure' ? 'bg-red-600' : 'border-slate-600 text-slate-300'}
                >
                  <Flame className="w-4 h-4 mr-2" />
                  Pressure
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Send To</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  variant={notificationData.recipientType === 'agent' ? 'default' : 'outline'}
                  onClick={() => setNotificationData({ ...notificationData, recipientType: 'agent' })}
                  className={notificationData.recipientType === 'agent' ? 'bg-purple-600' : 'border-slate-600 text-slate-300'}
                >
                  <User className="w-4 h-4 mr-2" />
                  Specific Agent
                </Button>
                <Button
                  variant={notificationData.recipientType === 'team' ? 'default' : 'outline'}
                  onClick={() => setNotificationData({ ...notificationData, recipientType: 'team' })}
                  className={notificationData.recipientType === 'team' ? 'bg-purple-600' : 'border-slate-600 text-slate-300'}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Entire Team
                </Button>
              </div>
            </div>
            {notificationData.recipientType === 'agent' && (
              <div>
                <Label className="text-slate-300">Select Agent</Label>
                <select
                  value={notificationData.recipientEmail}
                  onChange={(e) => setNotificationData({ ...notificationData, recipientEmail: e.target.value })}
                  className="w-full mt-2 p-2 rounded-md bg-slate-900/50 border border-slate-600 text-white"
                >
                  <option value="">Select an agent...</option>
                  {agents.map((agent) => (
                    <option key={agent.email} value={agent.email}>{agent.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <Label className="text-slate-300">Message</Label>
              <Textarea
                value={notificationData.message}
                onChange={(e) => setNotificationData({ ...notificationData, message: e.target.value })}
                className="bg-slate-900/50 border-slate-600 text-white mt-2"
                rows={3}
                placeholder={
                  notificationData.type === 'motivational' ? "Great work today! Keep it up..." :
                  notificationData.type === 'warning' ? "Please focus on improving your numbers..." :
                  "We need you to step up immediately..."
                }
              />
            </div>
            <Button 
              onClick={handleSendNotification} 
              className={`w-full ${
                notificationData.type === 'motivational' ? 'bg-emerald-600 hover:bg-emerald-700' :
                notificationData.type === 'warning' ? 'bg-amber-600 hover:bg-amber-700' :
                'bg-red-600 hover:bg-red-700'
              }`}
            >
              {getNotificationIcon(notificationData.type)}
              <span className="ml-2">Send Notification</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TodaySales;
